/**
 * DoctorAccessGate — subscription overlay wrapper for the doctor dashboard.
 *
 * Renders children (the dashboard) unconditionally. When the current
 * DOCTOR user is not allowed by the subscription gate (expired grace
 * or never subscribed), overlays a modal on top of the dashboard with
 * the "Start 30-day free trial" CTA — but the dashboard remains
 * visible behind it (dimmed) so the user can see their existing data.
 *
 * Prior behavior (deprecated 2026-08-14): fully replaced the dashboard
 * with <DoctorSubscriptionRequired>. Per user feedback, existing
 * doctors whose grace window has expired should see their dashboard
 * (context matters — they should know what they'd be losing) with the
 * subscribe prompt overlaid rather than a blank paywall shell.
 *
 * The initial fetch renders children immediately with a translucent
 * loading state — no flash-of-loading-spinner.
 */

import type { ReactNode } from 'react';
import { useDoctorSubscription } from '../hooks/useDoctorSubscription';
import { DoctorSubscriptionRequired } from './DoctorSubscriptionRequired';

interface Props {
  /** Whether the current user is a doctor / doctor-team-member. Callers
   *  typically compute this from `user?.role`. When false, the gate is
   *  bypassed entirely (no fetch, no wrapper). */
  isDoctorContext: boolean;
  children: ReactNode;
}

export function DoctorAccessGate({ isDoctorContext, children }: Props) {
  const { state } = useDoctorSubscription(isDoctorContext);

  if (!isDoctorContext) return <>{children}</>;

  // While the state is loading OR when the user is allowed, just render
  // the dashboard. No spinner, no flash.
  if (!state || state.allowed) return <>{children}</>;

  // User is blocked (expired or never_subscribed). Render the dashboard
  // behind a modal overlay so they can see their context while the
  // subscribe CTA sits on top.
  return (
    <div className="relative">
      {/* Dashboard rendered but dimmed and non-interactive */}
      <div aria-hidden className="pointer-events-none opacity-40">
        {children}
      </div>
      {/* Fixed-position overlay containing the subscribe CTA. Uses
          DoctorSubscriptionRequired but the wrapper positions it
          center-of-screen with a soft backdrop. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
        <DoctorSubscriptionRequired state={state} />
      </div>
    </div>
  );
}
